
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { verifyAdminAccess } from "@/services/user";

/**
 * Hook to consistently check if a user has admin status
 * Combines checks from both user metadata and profiles table
 * with detailed error reporting
 */
export const useAdminStatus = () => {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<any>(null);

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        setIsLoading(true);
        setError(null);

        console.log("Checking admin status...");
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          console.log("No active session found");
          setIsAdmin(false);
          setUser(null);
          setError("No active session");
          return;
        }

        setUser(session.user);
        
        try {
          // Use the comprehensive verification tool
          const verificationResult = await verifyAdminAccess();
          setDetails(verificationResult.details);
          
          if (verificationResult.isAdmin) {
            console.log("User verified as admin:", verificationResult);
            setIsAdmin(true);
            
            // Check if MFA is enabled in Supabase
            if (verificationResult.details?.mfaStatus?.errorDetails) {
              setError("Warning: MFA is disabled in Supabase settings. Some admin functions may be limited.");
            }
            
            return;
          }
          
          console.log("User is not an admin:", verificationResult);
          setIsAdmin(false);
          
          // Only set error if there's a specific issue to report
          if (verificationResult.details?.errorDetails) {
            setError(verificationResult.details.errorDetails);
          }
        } catch (verifyError: any) {
          console.error("Error in admin verification:", verifyError);
          setIsAdmin(false);
          setError(`Verification error: ${verifyError.message}`);
        }
      } catch (error: any) {
        console.error("Error checking admin status:", error);
        setIsAdmin(false);
        setError(`System error: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAdminStatus();
  }, []);

  return { isAdmin, isLoading, user, error, details };
};

export default useAdminStatus;
